import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import '../Shared/Training.css';
import '../Shared/OrganizationalTraining.css';
import {
  ArrowIcon,
  LessonsIcon,
  ProgressIcon,
  SearchIcon,
  TestsIcon,
  TimeIcon
} from '../Shared/TrainingIcons';
import KnowledgeBaseSidebar, {
  KnowledgeBaseSidebarItem
} from '../Shared/KnowledgeBaseSidebar';
import { getLessons, mapApiLessonToCourse, mapApiLessonToOrganizationalCourse } from '../../../api/knowledgeApi';
import type { Course, TrainingType } from './types';

const ITEMS_PER_PAGE = 4;

const statusLabels: Record<string, string> = {
  inProgress: 'В процессе',
  completed: 'Завершено',
  notStarted: 'Не начато'
};

const statusBadgeClassName: Record<string, string> = {
  inProgress: 'organizational-training__status-badge--progress',
  completed: 'organizational-training__status-badge--completed',
  notStarted: 'organizational-training__status-badge--idle'
};

interface TrainingListProps {
  trainingType: TrainingType;
}

const TrainingList: React.FC<TrainingListProps> = ({ trainingType }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { categoryId } = useParams<{ categoryId?: string }>();
  
  const isProfile = trainingType === 'profile';
  const isOrganizational = trainingType === 'organizational';
  
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>(() => {
    if (isProfile) {
      return categoryId || 'all';
    }
    const params = new URLSearchParams(location.search);
    const category = params.get('category');
    return category || 'all';
  });

  // Обновляем activeCategory при изменении categoryId в URL
  useEffect(() => {
    if (isProfile && categoryId) {
      setActiveCategory(categoryId);
    }
  }, [categoryId, isProfile]);
  const [statusFilter, setStatusFilter] = useState<'all' | string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dynamicCategories, setDynamicCategories] = useState<Array<{ id: string; name: string; label: string }>>([]);
  const [profileTags, setProfileTags] = useState<string[]>([]);
  const [organizationalTags, setOrganizationalTags] = useState<string[]>([]);

  // Загрузка данных из API
  useEffect(() => {
    const loadCourses = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await getLessons({
          type: trainingType,
          page: 1,
          page_size: 100,
        });
        
        const mappedCourses = isOrganizational
          ? response.results.map((apiLesson) => mapApiLessonToOrganizationalCourse(apiLesson, 'all'))
          : response.results.map((apiLesson) => mapApiLessonToCourse(apiLesson));
        
        setCourses(mappedCourses);

        // Формируем категории из тегов курсов
        const allTags = new Set<string>();
        mappedCourses.forEach((course) => {
          const tags: string[] = course.tags || [];
          tags.forEach((tag) => allTags.add(tag));
        });

        const categoriesFromTags = Array.from(allTags)
          .sort()
          .map((tag) => ({
            id: tag,
            name: tag,
            label: tag.charAt(0).toUpperCase() + tag.slice(1),
          }));

        setDynamicCategories(categoriesFromTags);
      } catch (err) {
        console.error('Ошибка при загрузке курсов:', err);
        setError('Не удалось загрузить курсы. Попробуйте обновить страницу.');
      } finally {
        setIsLoading(false);
      }
    };

    loadCourses();
  }, [trainingType, isOrganizational]);

  // Загружаем теги для обеих секций для навигации
  useEffect(() => {
    const loadTags = async () => {
      try {
        // Загружаем теги для профильного обучения
        const profileResponse = await getLessons({
          type: 'profile',
          page: 1,
          page_size: 100,
        });
        const profileCourses = profileResponse.results.map((apiLesson) => mapApiLessonToCourse(apiLesson));
        const profileTagsSet = new Set<string>();
        profileCourses.forEach((course) => {
          const tags: string[] = course.tags || [];
          tags.forEach((tag) => profileTagsSet.add(tag));
        });
        setProfileTags(Array.from(profileTagsSet).sort());

        // Загружаем теги для организационного обучения
        const orgResponse = await getLessons({
          type: 'organizational',
          page: 1,
          page_size: 100,
        });
        const orgCourses = orgResponse.results.map((apiLesson) =>
          mapApiLessonToOrganizationalCourse(apiLesson, 'all')
        );
        const orgTagsSet = new Set<string>();
        orgCourses.forEach((course) => {
          const tags: string[] = course.tags || [];
          tags.forEach((tag) => orgTagsSet.add(tag));
        });
        setOrganizationalTags(Array.from(orgTagsSet).sort());
      } catch (err) {
        console.error('Ошибка при загрузке тегов:', err);
      }
    };
    loadTags();
  }, []);

  const filteredCourses = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return courses.filter((course) => {
      const matchesCategory =
        activeCategory === 'all' ? true : (course.tags || []).includes(activeCategory);
      const matchesStatus = isOrganizational
        ? statusFilter === 'all' ? true : course.status === statusFilter
        : true;
      const matchesQuery =
        query.length === 0 ||
        course.title.toLowerCase().includes(query) ||
        course.description.toLowerCase().includes(query);
      return matchesCategory && matchesStatus && matchesQuery;
    });
  }, [courses, activeCategory, searchQuery, statusFilter, isOrganizational]);

  const totalPages = Math.max(1, Math.ceil(filteredCourses.length / ITEMS_PER_PAGE));
  const paginatedCourses = useMemo(() => {
    if (isProfile) {
      return filteredCourses; // Для профильного обучения не используем пагинацию
    }
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredCourses.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredCourses, currentPage, isProfile]);

  const handleCategorySelect = (categoryId: string) => {
    setActiveCategory(categoryId);
    setCurrentPage(1);
    
    if (isProfile) {
      navigate(`/knowledge-base/profile-training/${categoryId}`);
    } else {
      const params = new URLSearchParams(location.search);
      if (categoryId === 'all') {
        params.delete('category');
      } else {
        params.set('category', categoryId);
      }
      const query = params.toString();
      navigate(`/knowledge-base/organizational-training${query ? `?${query}` : ''}`, {
        replace: true
      });
    }
  };

  const handleStatusSelect = (status: 'all' | string) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleNavigateBack = () => {
    navigate('/knowledge-base');
  };

  useEffect(() => {
    if (isOrganizational) {
      const params = new URLSearchParams(location.search);
      const category = params.get('category');
      if (category) {
        setActiveCategory(category);
        setCurrentPage(1);
      }
    }
  }, [location.search, isOrganizational]);

  const currentSidebarItems: KnowledgeBaseSidebarItem[] = [
    {
      id: 'all',
      label: 'Все разделы',
      onClick: () => handleCategorySelect('all'),
      isActive: activeCategory === 'all'
    },
    ...dynamicCategories.map((category) => ({
      id: category.id,
      label: category.label,
      onClick: () => handleCategorySelect(category.id),
      isActive: activeCategory === category.id
    }))
  ];

  const profileSidebarItems: KnowledgeBaseSidebarItem[] = [
    {
      id: 'all',
      label: 'Все разделы',
      onClick: () => {
        if (isProfile) {
          handleCategorySelect('all');
        } else {
          navigate('/knowledge-base/profile-training/all');
        }
      },
      isActive: isProfile && activeCategory === 'all'
    },
    ...profileTags.map((tag) => ({
      id: tag,
      label: tag.charAt(0).toUpperCase() + tag.slice(1),
      onClick: () => {
        if (isProfile) {
          handleCategorySelect(tag);
        } else {
          navigate(`/knowledge-base/profile-training/${tag}`);
        }
      },
      isActive: isProfile && activeCategory === tag
    }))
  ];

  const organizationalSidebarItems: KnowledgeBaseSidebarItem[] = [
    {
      id: 'all',
      label: 'Все разделы',
      onClick: () => {
        if (isOrganizational) {
          handleCategorySelect('all');
        } else {
          navigate('/knowledge-base/organizational-training');
        }
      },
      isActive: isOrganizational && activeCategory === 'all'
    },
    ...organizationalTags.map((tag) => ({
      id: tag,
      label: tag.charAt(0).toUpperCase() + tag.slice(1),
      onClick: () => {
        if (isOrganizational) {
          handleCategorySelect(tag);
        } else {
          navigate(`/knowledge-base/organizational-training?category=${tag}`);
        }
      },
      isActive: isOrganizational && activeCategory === tag
    }))
  ];

  const handleCourseOpen = async (course: Course) => {
    if (course.lessons.length > 0) {
      if (isProfile) {
        navigate(
          `/knowledge-base/profile-training/${activeCategory}/${course.id}/lesson/${course.lessons[0].id}`
        );
      } else {
        navigate(
          `/knowledge-base/organizational-training/${course.id}/lesson/${course.lessons[0].id}`
        );
      }
      return;
    }

    // Если уроки не загружены, загружаем курс из API
    try {
      const { getLessonById } = await import('../../../api/knowledgeApi');
      const apiLesson = await getLessonById(course.id);
      const fullCourse = isOrganizational
        ? mapApiLessonToOrganizationalCourse(apiLesson, 'all')
        : mapApiLessonToCourse(apiLesson);
      
      if (fullCourse.lessons.length > 0) {
        if (isProfile) {
          navigate(
            `/knowledge-base/profile-training/${activeCategory}/${course.id}/lesson/${fullCourse.lessons[0].id}`
          );
        } else {
          navigate(
            `/knowledge-base/organizational-training/${course.id}/lesson/${fullCourse.lessons[0].id}`
          );
        }
      }
    } catch (error) {
      console.error('Ошибка при загрузке курса:', error);
    }
  };

  const renderLogo = (course: Course) => {
    if (course.logo) {
      return (
        <img
          src={course.logo}
          alt={course.title}
          className={isProfile ? "profile-training__course-logo-image" : "organizational-training__course-image"}
        />
      );
    }
    if (isProfile && course.providerLogo) {
      const providerLogos: Record<string, string> = {
        autodesk: 'A',
        revit: 'R'
      };
      return (
        <div
          className={`profile-training__course-logo profile-training__course-logo--${course.providerLogo || 'generic'}`}
        >
          {providerLogos[course.providerLogo] ?? course.providerLogo?.charAt(0)?.toUpperCase() ?? 'A'}
        </div>
      );
    }
    return (
      <div className="organizational-training__course-image-placeholder">
        <span>📘</span>
      </div>
    );
  };

  const title = isProfile ? 'Профильное обучение' : 'Организационное обучение';
  const basePath = isProfile ? '/knowledge-base/profile-training' : '/knowledge-base/organizational-training';

  return (
    <div className={`profile-training ${isOrganizational ? 'organizational-training' : ''}`}>
      <div className={`profile-training__container ${isOrganizational ? 'organizational-training__container' : ''}`}>
        <KnowledgeBaseSidebar
          activeSection={isProfile ? 'profile' : 'organizational'}
          profileItems={profileSidebarItems}
          organizationalItems={organizationalSidebarItems}
        />

        <main className="profile-training__main">
          <div className="profile-training__breadcrumbs">
            <button
              type="button"
              className="profile-training__breadcrumb-item profile-training__breadcrumb-link"
              onClick={() => navigate('/knowledge-base')}
            >
              Главная
            </button>
            <span className="profile-training__breadcrumb-separator">›</span>
            <span className="profile-training__breadcrumb-item">{title}</span>
          </div>

          <div className={`profile-training__header ${isOrganizational ? 'organizational-training__header' : ''}`}>
            <button className="profile-training__back-button" onClick={handleNavigateBack}>
              <span className="profile-training__back-arrow">
                <ArrowIcon />
              </span>
            </button>
            <h1 className="profile-training__title">{title}</h1>
          </div>

          {isOrganizational && (
            <div className="organizational-training__filters">
              <div className="profile-training__search organizational-training__search">
                <div className="profile-training__search-icon">
                  <SearchIcon />
                </div>
                <input
                  type="text"
                  className="profile-training__search-input"
                  placeholder="Поиск по курсам"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                />
              </div>
              <div className="organizational-training__status-filters">
                <button
                  type="button"
                  className={`organizational-training__status-filter${
                    statusFilter === 'all' ? ' organizational-training__status-filter--active' : ''
                  }`}
                  onClick={() => handleStatusSelect('all')}
                >
                  Все
                </button>
                <button
                  type="button"
                  className={`organizational-training__status-filter${
                    statusFilter === 'inProgress' ? ' organizational-training__status-filter--active' : ''
                  }`}
                  onClick={() => handleStatusSelect('inProgress')}
                >
                  В процессе
                </button>
                <button
                  type="button"
                  className={`organizational-training__status-filter${
                    statusFilter === 'completed' ? ' organizational-training__status-filter--active' : ''
                  }`}
                  onClick={() => handleStatusSelect('completed')}
                >
                  Завершено
                </button>
                <button
                  type="button"
                  className={`organizational-training__status-filter${
                    statusFilter === 'notStarted' ? ' organizational-training__status-filter--active' : ''
                  }`}
                  onClick={() => handleStatusSelect('notStarted')}
                >
                  Не начато
                </button>
              </div>
            </div>
          )}

          {isProfile && (
            <div className="profile-training__search">
              <div className="profile-training__search-icon">
                <SearchIcon />
              </div>
              <input
                type="text"
                className="profile-training__search-input"
                placeholder="Поиск"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </div>
          )}

          <div className={isOrganizational ? 'organizational-training__courses' : 'profile-training__courses'}>
            {isLoading && (
              <div className={isOrganizational ? 'organizational-training__empty' : ''} style={{ textAlign: 'center', padding: '40px' }}>
                <h3>Загрузка курсов...</h3>
              </div>
            )}
            {error && (
              <div className={isOrganizational ? 'organizational-training__empty' : ''} style={{ textAlign: 'center', padding: '40px', color: '#d32f2f' }}>
                <h3>Ошибка</h3>
                <p>{error}</p>
              </div>
            )}
            {!isLoading && !error && paginatedCourses.length === 0 && (
              <div className={isOrganizational ? 'organizational-training__empty' : ''} style={{ textAlign: 'center', padding: '40px' }}>
                <h3>Курсы не найдены</h3>
                <p>Попробуйте изменить запрос или фильтры.</p>
              </div>
            )}
            {!isLoading && !error && paginatedCourses.map((course: Course) => {
              if (isOrganizational) {
                return (
                  <article
                    key={course.id}
                    className="organizational-training__course-card"
                    onClick={() => handleCourseOpen(course)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleCourseOpen(course);
                      }
                    }}
                  >
                    <div className="organizational-training__course-preview">
                      {renderLogo(course)}
                    </div>
                    <div className="organizational-training__course-body">
                      <div className="organizational-training__course-meta-header">
                        <span className="organizational-training__badge">{course.badge || 'Курс'}</span>
                        {course.status && (
                          <span
                            className={`organizational-training__status-badge ${statusBadgeClassName[course.status]}`}
                          >
                            {statusLabels[course.status]}
                          </span>
                        )}
                      </div>
                      <h2 className="organizational-training__course-title">{course.title}</h2>
                      <p className="organizational-training__course-description">{course.description}</p>

                      <div className="organizational-training__course-stats">
                        <div className="profile-training__course-meta-item">
                          <LessonsIcon />
                          <span>{course.lessonsCount} уроков</span>
                        </div>
                        <div className="profile-training__course-meta-item">
                          <TestsIcon />
                          <span>{course.testsCount} тест</span>
                        </div>
                        <div className="profile-training__course-meta-item">
                          <TimeIcon />
                          <span>{course.duration}</span>
                        </div>
                        <div className="profile-training__course-meta-item">
                          <ProgressIcon />
                          <span>{course.completionLabel || `${course.completionPercent}% завершено`}</span>
                        </div>
                      </div>
                    </div>
                    <div className="organizational-training__course-actions">
                      <button
                        type="button"
                        className="organizational-training__course-more"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleCourseOpen(course);
                        }}
                      >
                        Подробнее →
                      </button>
                    </div>
                  </article>
                );
              }

              // Profile training card
              return (
                <article
                  key={course.id}
                  className={`profile-training__course-card${
                    course.completionPercent === 100 ? ' profile-training__course-card--completed' : ''
                  }`}
                  onClick={() => handleCourseOpen(course)}
                >
                  <div className="profile-training__course-card-main">
                    <div className="profile-training__course-brand">
                      {renderLogo(course)}
                      <div className="profile-training__course-brand-info">
                        <h2 className="profile-training__course-title">{course.title}</h2>
                        <span className="profile-training__course-provider">{course.provider || 'AQMOL'}</span>
                      </div>
                    </div>

                    <div className="profile-training__course-meta">
                      <div className="profile-training__course-meta-item">
                        <LessonsIcon />
                        <span>{course.lessonsCount} уроков</span>
                      </div>
                      <div className="profile-training__course-meta-item">
                        <TestsIcon />
                        <span>{course.testsCount} тест</span>
                      </div>
                      <div className="profile-training__course-meta-item">
                        <TimeIcon />
                        <span>{course.duration}</span>
                      </div>
                      <div className="profile-training__course-meta-item">
                        <ProgressIcon />
                        <span>{course.completionPercent}% завершено ({Math.round((course.completionPercent / 100) * course.lessonsCount)}/{course.lessonsCount})</span>
                      </div>
                    </div>
                  </div>

                  <div className="profile-training__course-card-footer">
                    <span
                      className={`profile-training__course-badge profile-training__course-badge--${course.badgeStatus || 'default'}`}
                    >
                      {course.badgeText || 'Курс'}
                    </span>
                    <button
                      type="button"
                      className="profile-training__course-details"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleCourseOpen(course);
                      }}
                    >
                      Подробнее →
                    </button>
                  </div>
                </article>
              );
            })}
          </div>

          {isOrganizational && (
            <div className="organizational-training__pagination">
              {Array.from({ length: totalPages }).map((_, index) => {
                const page = index + 1;
                return (
                  <button
                    key={page}
                    type="button"
                    className={`organizational-training__pagination-button${
                      page === currentPage ? ' organizational-training__pagination-button--active' : ''
                    }`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                );
              })}
            </div>
          )}
        </main>

        <aside className="profile-training__recent">
          <div className="profile-training__recent-card">
            <h3 className="profile-training__recent-title">Вы недавно смотрели:</h3>
            <div className="profile-training__recent-list">
              <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                <p>Нет недавно просмотренных курсов</p>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default TrainingList;

